import { useState, useEffect, useCallback } from 'react';
import API_BASE from '../config';

const CHANNELS = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'x', label: 'X' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'google_business', label: 'Google Business' }
];

const STATUS_COLOURS = {
  pending: { bg: '#FEF3C7', fg: '#92400E', label: 'Pending' },
  approved: { bg: '#D1FAE5', fg: '#065F46', label: 'Approved' },
  rejected: { bg: '#FEE2E2', fg: '#991B1B', label: 'Rejected' }
};

function formatDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatCost(n) {
  if (n === null || n === undefined) return '—';
  return '$' + parseFloat(n).toFixed(4);
}

function formatTime(s) {
  if (!s) return '';
  return new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(s) {
  if (!s) return 'Unknown';
  const d = new Date(s);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  const diffDays = Math.floor((today - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return d.toLocaleDateString('en-GB', { weekday: 'long' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminSocial() {
  const [tab, setTab] = useState('library');
  const [clients, setClients] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterClientId, setFilterClientId] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [dayCounts, setDayCounts] = useState([]);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('with_channels');
  const [exportSchedule, setExportSchedule] = useState('manual');
  const [exporting, setExporting] = useState(false);
  const [linkedinTarget, setLinkedinTarget] = useState('page');
  const [defaultLink, setDefaultLink] = useState('');

  const [genClientId, setGenClientId] = useState('');
  const [genChannels, setGenChannels] = useState(['linkedin']);
  const [genSourceType, setGenSourceType] = useState('icp');
  const [genTopic, setGenTopic] = useState('');
  const [genVariants, setGenVariants] = useState(1);
  const [genCtaUrl, setGenCtaUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [genResult, setGenResult] = useState(null);

  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/clients`).then(r => r.json()).then(data => {
      const list = Array.isArray(data) ? data : [];
      setClients(list);
      if (list.length && !genClientId) setGenClientId(list[0].id);
    }).catch(() => {});
  }, []);

  const loadPosts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterClientId) params.append('clientId', filterClientId);
    if (filterChannel) params.append('channel', filterChannel);
    if (filterStatus) params.append('status', filterStatus);
    if (filterFrom) params.append('from', filterFrom);
    if (filterTo) params.append('to', filterTo);
    params.append('page', page);
    params.append('pageSize', pageSize);
    fetch(`${API_BASE}/social/posts?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        setPosts(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(data?.total || 0);
        setDayCounts(Array.isArray(data?.dayCounts) ? data.dayCounts : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filterClientId, filterChannel, filterStatus, filterFrom, filterTo, page, pageSize]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  function toggleChannel(key) {
    setGenChannels(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);
  }

  async function handleGenerate() {
    setGenError(null);
    setGenResult(null);
    if (!genClientId) { setGenError('Pick a client'); return; }
    if (!genChannels.length) { setGenError('Pick at least one channel'); return; }
    if (genSourceType === 'topic' && !genTopic.trim()) { setGenError('Enter a topic'); return; }
    setGenerating(true);
    try {
      const response = await fetch(`${API_BASE}/social/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: parseInt(genClientId),
          channels: genChannels,
          sourceType: genSourceType,
          topic: genSourceType === 'topic' ? genTopic.trim() : null,
          variants: parseInt(genVariants),
          ctaUrl: genCtaUrl.trim() || null
        })
      });
      const data = await response.json();
      if (!response.ok) { setGenError(data.error || 'Generation failed'); setGenerating(false); return; }
      setGenResult(data);
      setGenerating(false);
      loadPosts();
      setTab('library');
    } catch (err) {
      setGenError(err.message);
      setGenerating(false);
    }
  }

  function openEditor(post) {
    setEditingPost(post);
    setEditContent(post.content);
  }

  async function saveEdit() {
    const response = await fetch(`${API_BASE}/social/posts/${editingPost.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: editContent })
    });
    if (response.ok) {
      setEditingPost(null);
      loadPosts();
    }
  }

  async function rejectPost(id) {
    if (!window.confirm('Reject this post?')) return;
    await fetch(`${API_BASE}/social/posts/${id}/reject`, { method: 'POST' });
    loadPosts();
  }

  async function deletePost(id) {
    if (!window.confirm('Delete this post permanently?')) return;
    await fetch(`${API_BASE}/social/posts/${id}`, { method: 'DELETE' });
    loadPosts();
  }

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleSelectAllVisible() {
    const visibleIds = posts.map(p => p.id);
    const allSelected = visibleIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  }

  async function exportToZoho() {
    if (!selectedIds.length) return;
    setExporting(true);
    try {
      const response = await fetch(`${API_BASE}/social/export-zoho-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_ids: selectedIds, format: exportFormat, schedule_strategy: exportSchedule, linkedin_target: linkedinTarget, default_link: defaultLink })
      });
      if (!response.ok) {
        const data = await response.json();
        alert('Export failed: ' + (data.error || response.status));
        setExporting(false);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : `zoho_social_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setShowExportModal(false);
      setSelectedIds([]);
      setExporting(false);
      loadPosts();
    } catch (err) {
      alert('Export error: ' + err.message);
      setExporting(false);
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
  }

  const styles = {
    page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' },
    h1: { fontSize: 24, fontWeight: 600, marginBottom: 4, color: '#1F2937' },
    sub: { fontSize: 14, color: '#6B7280', marginBottom: 24 },
    tabs: { display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: 24 },
    tab: (active) => ({
      padding: '12px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 500,
      color: active ? '#8B1A1A' : '#6B7280',
      borderBottom: active ? '2px solid #8B1A1A' : '2px solid transparent',
      marginBottom: -1
    }),
    card: { background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, padding: 20, marginBottom: 16 },
    label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 },
    input: { width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #D1D5DB', borderRadius: 6, background: 'white', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #D1D5DB', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box', minHeight: 80, resize: 'vertical' },
    row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 },
    btnPrimary: { background: '#8B1A1A', color: 'white', border: 'none', padding: '10px 20px', fontSize: 14, fontWeight: 500, borderRadius: 6, cursor: 'pointer' },
    btnSecondary: { background: 'white', color: '#374151', border: '1px solid #D1D5DB', padding: '8px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer' },
    btnDanger: { background: 'white', color: '#991B1B', border: '1px solid #FCA5A5', padding: '8px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer' },
    btnSuccess: { background: '#065F46', color: 'white', border: 'none', padding: '8px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer' },
    chip: (active) => ({
      display: 'inline-block', padding: '6px 12px', marginRight: 8, marginBottom: 8,
      fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer',
      background: active ? '#8B1A1A' : 'white',
      color: active ? 'white' : '#374151',
      border: active ? '1px solid #8B1A1A' : '1px solid #D1D5DB'
    }),
    statusPill: (status) => {
      const c = STATUS_COLOURS[status] || { bg: '#F3F4F6', fg: '#374151', label: status };
      return { display: 'inline-block', padding: '2px 10px', fontSize: 12, fontWeight: 500, borderRadius: 12, background: c.bg, color: c.fg };
    },
    postCard: { background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, padding: 20, marginBottom: 12 },
    postMeta: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', fontSize: 12, color: '#6B7280' },
    postContent: { fontSize: 14, lineHeight: 1.6, color: '#1F2937', whiteSpace: 'pre-wrap', marginBottom: 12 },
    postActions: { display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #F3F4F6', paddingTop: 12 },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalCard: { background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' },
    dayHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', marginBottom: 8, borderBottom: '1px solid #E5E7EB', fontSize: 13, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
    dayCount: { fontSize: 12, fontWeight: 500, color: '#6B7280', textTransform: 'none', letterSpacing: 'normal' },
    paginationBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', marginTop: 8 },
    bulkBar: { display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, marginBottom: 16, flexWrap: 'wrap' }
  };

  return (
    <div style={styles.page}>
      <div style={styles.h1}>Social Posts</div>
      <div style={styles.sub}>Generate, edit, and approve posts across LinkedIn, X, Facebook, Instagram, TikTok, and Google Business.</div>

      <div style={styles.tabs}>
        <div style={styles.tab(tab === 'library')} onClick={() => setTab('library')}>Library</div>
        <div style={styles.tab(tab === 'generate')} onClick={() => setTab('generate')}>Generate</div>
      </div>

      {tab === 'generate' && (
        <div style={styles.card}>
          <div style={{ marginBottom: 16 }}>
            <div style={styles.label}>Client</div>
            <select style={styles.select} value={genClientId} onChange={e => {
              setGenClientId(e.target.value);
              const c = clients.find(cl => String(cl.id) === String(e.target.value));
              if (c && (c.website || c.url)) setGenCtaUrl(c.website || c.url);
            }}>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name || c.workspace_name || `Client #${c.id}`}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={styles.label}>Channels</div>
            {CHANNELS.map(c => (
              <span key={c.key} style={styles.chip(genChannels.includes(c.key))} onClick={() => toggleChannel(c.key)}>{c.label}</span>
            ))}
          </div>

          <div style={styles.row}>
            <div>
              <div style={styles.label}>Source</div>
              <select style={styles.select} value={genSourceType} onChange={e => setGenSourceType(e.target.value)}>
                <option value="icp">From active ICP</option>
                <option value="topic">From a topic I type</option>
              </select>
            </div>
            <div>
              <div style={styles.label}>Variants per channel</div>
              <select style={styles.select} value={genVariants} onChange={e => setGenVariants(e.target.value)}>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
              </select>
            </div>
          </div>

          {genSourceType === 'topic' && (
            <div style={{ marginBottom: 16 }}>
              <div style={styles.label}>Topic</div>
              <textarea style={styles.textarea} value={genTopic} onChange={e => setGenTopic(e.target.value)} placeholder="e.g. Why CRMs fail without sales process" />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={styles.label}>CTA URL (appended to every post as "Visit {URL}")</div>
            <input
              type="url"
              style={styles.input}
              value={genCtaUrl}
              onChange={e => setGenCtaUrl(e.target.value)}
              placeholder="https://useclientengine.com or leave blank for no CTA"
            />
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
              Pre-filled from the client's website. Override with a campaign-specific URL like https://useclientengine.com if you're promoting a specific page.
            </div>
          </div>

          <button style={styles.btnPrimary} onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating...' : `Generate ${genChannels.length * genVariants} post${genChannels.length * genVariants === 1 ? '' : 's'}`}
          </button>

          {genError && <div style={{ marginTop: 12, color: '#991B1B', fontSize: 13 }}>{genError}</div>}
          {genResult && (
            <div style={{ marginTop: 16, fontSize: 13, color: '#065F46' }}>
              Generated {genResult.created_count} post{genResult.created_count === 1 ? '' : 's'} for ${genResult.total_cost_usd?.toFixed(4) || '0.0000'}.
              {genResult.errors?.length > 0 && <div style={{ color: '#991B1B', marginTop: 4 }}>Errors: {JSON.stringify(genResult.errors)}</div>}
            </div>
          )}
        </div>
      )}

      {tab === 'library' && (
        <>
          <div style={styles.card}>
            <div style={styles.row}>
              <div>
                <div style={styles.label}>Client</div>
                <select style={styles.select} value={filterClientId} onChange={e => { setPage(0); setFilterClientId(e.target.value); }}>
                  <option value="">All clients</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name || c.workspace_name || `Client #${c.id}`}</option>)}
                </select>
              </div>
              <div>
                <div style={styles.label}>Channel</div>
                <select style={styles.select} value={filterChannel} onChange={e => { setPage(0); setFilterChannel(e.target.value); }}>
                  <option value="">All channels</option>
                  {CHANNELS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <div style={styles.label}>Status</div>
                <select style={styles.select} value={filterStatus} onChange={e => { setPage(0); setFilterStatus(e.target.value); }}>
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <div style={styles.label}>From</div>
                <input type="date" style={styles.input} value={filterFrom} onChange={e => { setPage(0); setFilterFrom(e.target.value); }} />
              </div>
              <div>
                <div style={styles.label}>To</div>
                <input type="date" style={styles.input} value={filterTo} onChange={e => { setPage(0); setFilterTo(e.target.value); }} />
              </div>
            </div>
            {(filterClientId || filterChannel || filterStatus || filterFrom || filterTo) && (
              <button
                style={{ ...styles.btnSecondary, marginTop: 8 }}
                onClick={() => { setPage(0); setFilterClientId(''); setFilterChannel(''); setFilterStatus(''); setFilterFrom(''); setFilterTo(''); }}
              >
                Clear filters
              </button>
            )}
          </div>

          {posts.length > 0 && (
            <div style={styles.bulkBar}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={posts.length > 0 && posts.every(p => selectedIds.includes(p.id))}
                  onChange={toggleSelectAllVisible}
                />
                Select all on this page
              </label>
              <span style={{ fontSize: 13, color: '#6B7280' }}>{selectedIds.length} selected</span>
              <button
                style={{ ...styles.btnPrimary, opacity: selectedIds.length ? 1 : 0.4, cursor: selectedIds.length ? 'pointer' : 'not-allowed' }}
                disabled={!selectedIds.length}
                onClick={() => {
                  // Pre-fill link from first selected post's client website
                  const firstPost = posts.find(p => selectedIds.includes(p.id));
                  if (firstPost) {
                    const clientObj = clients.find(c => c.id === firstPost.client_id);
                    if (clientObj && (clientObj.website || clientObj.url)) {
                      setDefaultLink(clientObj.website || clientObj.url);
                    }
                  }
                  setShowExportModal(true);
                }}
              >
                Export {selectedIds.length} to Zoho Social CSV
              </button>
              {selectedIds.length > 0 && (
                <button style={styles.btnSecondary} onClick={() => setSelectedIds([])}>Clear selection</button>
              )}
            </div>
          )}

          {loading && <div style={{ color: '#6B7280', fontSize: 14 }}>Loading...</div>}
          {!loading && posts.length === 0 && <div style={{ color: '#6B7280', fontSize: 14 }}>No posts yet. Switch to Generate to create some.</div>}

          {!loading && posts.length > 0 && (() => {
            // Group posts by day label
            const groups = {};
            const order = [];
            posts.forEach(p => {
              const label = dayLabel(p.created_at);
              if (!groups[label]) { groups[label] = []; order.push(label); }
              groups[label].push(p);
            });
            return order.map(label => (
              <div key={label} style={{ marginBottom: 24 }}>
                <div style={styles.dayHeader}>
                  <span>{label}</span>
                  <span style={styles.dayCount}>{groups[label].length} post{groups[label].length === 1 ? '' : 's'}</span>
                </div>
                {groups[label].map(p => {
                  const channelLabel = CHANNELS.find(c => c.key === p.channel)?.label || p.channel;
                  return (
                    <div key={p.id} style={styles.postCard}>
                      <div style={styles.postMeta}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(p.id)}
                          onChange={() => toggleSelect(p.id)}
                          style={{ marginRight: 4 }}
                        />
                        <span style={{ fontWeight: 600, color: '#1F2937' }}>{channelLabel}</span>
                        <span style={styles.statusPill(p.status)}>{STATUS_COLOURS[p.status]?.label || p.status}</span>
                        <span>Client #{p.client_id}</span>
                        <span>{p.source_type === 'topic' ? `Topic: ${p.source_input}` : 'From ICP'}</span>
                        <span>Model: {p.model_used}</span>
                        <span>Cost: {formatCost(p.cost_usd)}</span>
                        <span>{formatTime(p.created_at)}</span>
                        {p.fallback_used && <span style={{ color: '#92400E' }}>(fallback)</span>}
                      </div>
                      <div style={styles.postContent}>{p.content}</div>
                      <div style={styles.postActions}>
                        <button style={styles.btnSecondary} onClick={() => copyToClipboard(p.content)}>Copy</button>
                        <button style={styles.btnSecondary} onClick={() => openEditor(p)}>Edit</button>
                        {p.status === 'pending' && <button style={styles.btnDanger} onClick={() => rejectPost(p.id)}>Reject</button>}
                        <button style={styles.btnDanger} onClick={() => deletePost(p.id)}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })()}

          {!loading && total > pageSize && (
            <div style={styles.paginationBar}>
              <button style={styles.btnSecondary} disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Previous</button>
              <span style={{ fontSize: 13, color: '#6B7280' }}>
                Page {page + 1} of {Math.ceil(total / pageSize)} ({total} total)
              </span>
              <button style={styles.btnSecondary} disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {showExportModal && (
        <div style={styles.modal} onClick={() => setShowExportModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Export to Zoho Social</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
              Generates a CSV in Zoho Social bulk-schedule format. Upload it inside Zoho Social via Posts &gt; Bulk Schedule.
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={styles.label}>CSV format</div>
              <select style={styles.select} value={exportFormat} onChange={e => setExportFormat(e.target.value)}>
                <option value="with_channels">With Channels (recommended) — one row per post, channel set per row</option>
                <option value="standard">Standard — content only, channels picked at upload</option>
                <option value="multi_image">Multi-Image — same as standard but supports up to 8 images per post (image fields blank for now)</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={styles.label}>LinkedIn target (only affects LinkedIn posts)</div>
              <select style={styles.select} value={linkedinTarget} onChange={e => setLinkedinTarget(e.target.value)}>
                <option value="page">Company Page only</option>
                <option value="profile">Personal Profile only</option>
                <option value="both">Both (creates two rows per LinkedIn post)</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={styles.label}>Link URL (required by Zoho)</div>
              <input
                type="url"
                style={styles.input}
                value={defaultLink}
                onChange={e => setDefaultLink(e.target.value)}
                placeholder="https://yourclient.com"
              />
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                Zoho Social rejects rows with empty link URLs. Pre-filled from the client's website. Edit if you want a campaign-specific landing page.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={styles.label}>Schedule</div>
              <select style={styles.select} value={exportSchedule} onChange={e => setExportSchedule(e.target.value)}>
                <option value="manual">Manual — leave dates blank, you set them inside Zoho</option>
                <option value="spread">Spread — auto-distribute across next 7 days at 10am and 2pm</option>
                <option value="now">Now — leave blank so Zoho posts immediately on upload</option>
              </select>
            </div>

            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 16, padding: 10, background: '#F9FAFB', borderRadius: 6 }}>
              {selectedIds.length} post{selectedIds.length === 1 ? '' : 's'} selected. After export, posts will be marked as Approved with Zoho-sent timestamp.
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={styles.btnSecondary} onClick={() => setShowExportModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={exportToZoho} disabled={exporting}>
                {exporting ? 'Exporting...' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPost && (
        <div style={styles.modal} onClick={() => setEditingPost(null)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Edit post</div>
            <textarea
              style={{ ...styles.textarea, minHeight: 300 }}
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
            />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={styles.btnSecondary} onClick={() => setEditingPost(null)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
